/**
 * Routing.
 *
 * Everything except sign-in and invitation redemption sits behind RequireSession.
 * A deep link followed while signed out is preserved and returned to afterwards —
 * which is the whole point of the "sign in to approve" links in notification emails
 * (docs/07).
 */

import { Suspense, lazy, type ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { Card, LoadingState, PageHeader, Spinner } from './components/ui.tsx';
import { useSession } from './lib/session.tsx';
import { AcceptInvitation, SignIn } from './routes/SignIn.tsx';
import { Approvals, Requests } from './routes/Requests.tsx';
import { RequestDetail } from './routes/RequestDetail.tsx';
import { NewRequest } from './routes/NewRequest.tsx';

/**
 * Loads a code-split screen, surviving a redeploy.
 *
 * Every build produces new filenames. A browser holding the previous page then asks for
 * a chunk that no longer exists, the dynamic import rejects, and — with nothing catching
 * it — React renders an empty panel. That is not a bug in the screen; the tab simply goes
 * blank with no message, which is exactly what happened after a deploy.
 *
 * So: on a failed import, reload once to pick up the current filenames. The sentinel
 * stops a genuinely missing chunk from becoming a reload loop; the second failure falls
 * through to the error boundary, which says what happened.
 */
function screen<T extends ComponentType<unknown>>(load: () => Promise<{ default: T }>) {
  return lazy(() =>
    load().catch((err) => {
      const KEY = 'chunk-reload';
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, '1');
        window.location.reload();
        // Never resolves — the reload takes over before React can render anything.
        return new Promise<{ default: T }>(() => {});
      }
      sessionStorage.removeItem(KEY);
      throw err;
    }),
  );
}

// Charts are a large dependency and only the dashboard needs them. Splitting keeps
// the approval screen — the one opened on a phone from an email — small.
const Dashboard = screen(() => import('./routes/Dashboard.tsx').then((m) => ({ default: m.Dashboard })));
const Reports = screen(() => import('./routes/Reports.tsx').then((m) => ({ default: m.Reports })));
const Admin = screen(() => import('./routes/Admin.tsx').then((m) => ({ default: m.Admin })));
const Finance = screen(() => import('./routes/Finance.tsx').then((m) => ({ default: m.Finance })));
const Budgets = screen(() => import('./routes/Budgets.tsx').then((m) => ({ default: m.Budgets })));
const Orders = screen(() => import('./routes/Orders.tsx').then((m) => ({ default: m.Orders })));
const Notifications = screen(() => import('./routes/Notifications.tsx').then((m) => ({ default: m.Notifications })));
const Account = screen(() => import('./routes/Account.tsx').then((m) => ({ default: m.Account })));
const Buying = screen(() => import('./routes/Buying.tsx').then((m) => ({ default: m.Buying })));
const Overtime = screen(() => import('./routes/Overtime.tsx').then((m) => ({ default: m.Overtime })));

/**
 * Keeps the lazy-loaded routes from flashing an empty page, and stops a crash inside
 * one screen from blanking the whole shell. The area name appears in the message, so a
 * report says which screen rather than "it went blank".
 */
const load = (area: string, node: React.ReactNode) => (
  <ErrorBoundary area={area}>
    <Suspense fallback={<Card><LoadingState /></Card>}>{node}</Suspense>
  </ErrorBoundary>
);

/** The eagerly-bundled screens need the boundary too, without the Suspense wrapper. */
const guard = (area: string, node: React.ReactNode) => (
  <ErrorBoundary area={area}>{node}</ErrorBoundary>
);

export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/invite" element={<AcceptInvitation />} />

      <Route
        element={
          <RequireSession>
            <AppShell />
          </RequireSession>
        }
      >
        <Route
          index
          element={
            <ErrorBoundary area="Dashboard">
              <Suspense fallback={<Card><LoadingState label="Loading dashboard" /></Card>}>
                <Dashboard />
              </Suspense>
            </ErrorBoundary>
          }
        />
        <Route path="requests" element={guard('Requests', <Requests />)} />
        <Route path="requests/new" element={guard('New request', <NewRequest />)} />
        <Route path="requests/:id" element={guard('Request', <RequestDetail />)} />
        <Route path="approvals" element={guard('Approvals', <Approvals />)} />

        <Route path="finance" element={load('Finance', <Finance />)} />
        <Route path="budgets" element={load('Budgets', <Budgets />)} />
        <Route path="buying" element={load('Group buying', <Buying />)} />
        <Route path="overtime" element={load('Overtime', <Overtime />)} />
        <Route path="orders" element={load('Orders and invoices', <Orders />)} />
        <Route path="reports" element={load('Reports', <Reports />)} />
        <Route path="admin" element={load('Administration', <Admin />)} />

        <Route path="notifications" element={load('Notifications', <Notifications />)} />

        <Route path="account" element={load('Your account', <Account />)} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function RequireSession({ children }: { children: React.ReactNode }) {
  const { session, loading } = useSession();
  const location = useLocation();

  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <div className="flex items-center gap-2 text-[var(--ink-muted)]">
          <Spinner className="size-5" />
          <span className="text-[13px]">Checking your session…</span>
        </div>
      </div>
    );
  }

  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }

  return <>{children}</>;
}

function ComingSoon({ area }: { area: string }) {
  return (
    <>
      <PageHeader title={area} description="This part of the system is next to be built." />
      <Card>
        <p className="py-8 text-center text-[13px] text-[var(--ink-muted)]">
          The data and rules behind {area.toLowerCase()} are already in place and tested — the
          screens come next.
        </p>
      </Card>
    </>
  );
}

function NotFound() {
  return (
    <>
      <PageHeader title="Page not found" />
      <Card>
        <p className="py-8 text-center text-[13px] text-[var(--ink-muted)]">
          That page does not exist.
        </p>
      </Card>
    </>
  );
}
